import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SurveysService, type QuestionCategory, type QuestionCategoryCreate, type QuestionCategoryUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useQuestionCategories = () =>
  useQuery({
    queryKey: ["question-categories"],
    queryFn: async () => {
      const data = await fetchAll<QuestionCategory>(SurveysService.getApiQuestionCategories);
      return data.sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
    },
    staleTime: 5 * 60_000,
  });

export const useCreateQuestionCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: QuestionCategoryCreate) => {
      const res = await SurveysService.postApiQuestionCategories({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as QuestionCategory;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["question-categories"] }),
  });
};

export const useUpdateQuestionCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: QuestionCategoryUpdate }) => {
      const res = await SurveysService.putApiQuestionCategoriesById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as QuestionCategory;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["question-categories"] });
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
  });
};

export const useDeleteQuestionCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await SurveysService.deleteApiQuestionCategoriesById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["question-categories"] });
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
  });
};
